// src/constants/kyc.ts
/**
 * GwG/KYC constants (Issue #990).
 *
 * Single source of truth for the enum values of the compliance/KYC module —
 * never inline these strings in services, stores or components.
 */
import type {
  KycAnalysisStatusValue,
  KycCaptureMode,
  KycDocumentType,
  KycImageQualityValue,
  KycLivenessChallenge,
  KycRunStatusValue,
  KycSelfSlot,
  KycStartMethod,
  KycStatusValue,
  KycStepCodeValue,
  KycStepStatusValue,
  RegulatedCategory,
  RegulatedStatusValue,
} from '@/types/kyc'

export const KYC_STATUS: { readonly [K in KycStatusValue]: K } = {
  NONE: 'NONE',
  PENDING: 'PENDING',
  VERIFIED: 'VERIFIED',
  FAILED: 'FAILED',
  REVOKED: 'REVOKED',
}

export const KYC_START_METHOD: { readonly [K in KycStartMethod]: K } = {
  AUTO_IDENT: 'AUTO_IDENT',
  SWISSID: 'SWISSID',
  SELF_IDENT: 'SELF_IDENT',
}

export const REGULATED_CATEGORY: { readonly [K in RegulatedCategory]: K } = {
  REGULATED: 'REGULATED',
  LISTED: 'LISTED',
  PUBLIC_LAW: 'PUBLIC_LAW',
}

/** Selectable categories in the self-declaration, in display order. */
export const REGULATED_CATEGORIES: readonly RegulatedCategory[] = [
  REGULATED_CATEGORY.REGULATED,
  REGULATED_CATEGORY.LISTED,
  REGULATED_CATEGORY.PUBLIC_LAW,
]

/** Company states that exempt its contacts from the identification duty. */
export const EXEMPT_REGULATED_STATUSES: readonly RegulatedStatusValue[] = REGULATED_CATEGORIES

/**
 * Roles that get the first-login identification dialog.
 *
 * Investors and sellers both use the `investor` role; `signer` covers external
 * contract parties. Internal staff (admin/tenant_admin/sales) is never prompted.
 */
export const KYC_PROMPT_ROLES: readonly string[] = ['investor', 'signer']

/**
 * Window event that asks the layout to open the compliance dialog (Issue #1161).
 *
 * Identification and the Lex Koller assessment are one onboarding step (Wiki
 * R5), and the dialog for both lives in `DashboardLayout`. The dashboard hint
 * sits inside the `<Outlet />` of that layout, so it cannot reach its state —
 * it dispatches this event on `window` instead of linking into the settings.
 */
export const COMPLIANCE_DIALOG_OPEN_EVENT = 'compliance-dialog:open'

/** Hosts (exact match) that never serve production traffic. */
const NON_PRODUCTION_HOSTS: readonly string[] = ['localhost', '127.0.0.1', '[::1]', '::1']

/**
 * Domain suffix of the ephemeral review apps
 * (`<slug>-app.review.immotron.anadat.ch`, see `.gitlab-ci.yml`).
 *
 * Only this dedicated sub-domain is matched, never `*.immotron.anadat.ch` as a
 * whole: which host serves production is not decidable in this repo
 * (`CLAUDE.md` lists `app.immotron.anadat.ch` as the live URL, the CI calls the
 * same host `staging`), so a wider suffix could open the route in production.
 * Staging is enabled through the build flag instead, see
 * {@link KYC_STUB_ROUTE_ENABLED}.
 */
const REVIEW_APP_HOST_SUFFIX = '.review.immotron.anadat.ch'

/**
 * Whether `hostname` belongs to a non-production environment.
 *
 * Exported for testing; use {@link KYC_STUB_ROUTE_ENABLED} in application code.
 */
export function isNonProductionHost(hostname: string): boolean {
  const host = hostname.toLowerCase()
  return NON_PRODUCTION_HOSTS.includes(host) || host.endsWith(REVIEW_APP_HOST_SUFFIX)
}

/**
 * Whether the ident-provider stub page is routed.
 *
 * Dev builds always get it. Deployed builds get it when the image was built
 * with `VITE_KYC_STUB_ENABLED=true` — the CI passes that build arg for staging
 * and for the review apps, but not for the release image (see `Dockerfile` and
 * `.gitlab-ci.yml`). The hostname check is a second, independent guard for the
 * review apps, whose domain is unambiguously non-production.
 *
 * Production builds carry neither, so the route does not exist there at all.
 */
export const KYC_STUB_ROUTE_ENABLED: boolean =
  !import.meta.env.PROD ||
  import.meta.env.VITE_KYC_STUB_ENABLED === 'true' ||
  (typeof window !== 'undefined' && isNonProductionHost(window.location.hostname))

// ---------------------------------------------------------------------------
// Check run with visible single steps (Issue #991)
// ---------------------------------------------------------------------------

export const KYC_STEP_CODE: { readonly [K in KycStepCodeValue]: K } = {
  IDENT: 'IDENT',
  ZEFIX: 'ZEFIX',
  SIGNATORY: 'SIGNATORY',
  SECO: 'SECO',
  TRANSPARENCY_REGISTER: 'TRANSPARENCY_REGISTER',
  REGULATED: 'REGULATED',
}

export const KYC_STEP_STATUS: { readonly [K in KycStepStatusValue]: K } = {
  PENDING: 'PENDING',
  RUNNING: 'RUNNING',
  OK: 'OK',
  HIT: 'HIT',
  SKIPPED: 'SKIPPED',
  ERROR: 'ERROR',
}

export const KYC_RUN_STATUS: { readonly [K in KycRunStatusValue]: K } = {
  RUNNING: 'RUNNING',
  DONE: 'DONE',
  FAILED: 'FAILED',
}

/**
 * Step outcomes that need a manual follow-up before the identification counts.
 *
 * Deliberately shown in a friendly amber, never in alarm red — a list hit is
 * an internal matter, not the customer's problem.
 */
export const KYC_STEP_FOLLOW_UP_STATUSES: readonly KycStepStatusValue[] = [
  KYC_STEP_STATUS.HIT,
  KYC_STEP_STATUS.ERROR,
  KYC_STEP_STATUS.PENDING,
]

/** Poll interval of `GET /kyc/checks/{id}` while a run is in progress. */
export const KYC_RUN_POLL_INTERVAL_MS = 1500

/**
 * Minimum time a step stays on screen before the next one is revealed.
 *
 * The backend is often faster than the eye — without this floor the whole
 * list would flip to «done» in one frame and nobody would see what we check.
 */
export const KYC_STEP_MIN_VISIBLE_MS = 600

/** Safety net: stop polling a run that never reaches DONE/FAILED. */
export const KYC_RUN_MAX_POLLS = 80

// ---------------------------------------------------------------------------
// Self identification: capture front/back/selfie or a PDF copy (Issue #1070)
// ---------------------------------------------------------------------------

export const KYC_SELF_SLOT: { readonly [K in KycSelfSlot]: K } = {
  front: 'front',
  back: 'back',
  selfie: 'selfie',
  pdf: 'pdf',
}

/** All file slots, in capture order. */
export const KYC_SELF_SLOTS: readonly KycSelfSlot[] = [
  KYC_SELF_SLOT.front,
  KYC_SELF_SLOT.back,
  KYC_SELF_SLOT.selfie,
  KYC_SELF_SLOT.pdf,
]

export const KYC_CAPTURE_MODE: { readonly [K in Uppercase<KycCaptureMode>]: Lowercase<K> } = {
  LIVE: 'live',
  UPLOAD: 'upload',
}

export const KYC_DOCUMENT_TYPE: { readonly [K in KycDocumentType]: K } = {
  ID_CARD: 'ID_CARD',
  PASSPORT: 'PASSPORT',
  RESIDENCE_PERMIT: 'RESIDENCE_PERMIT',
  OTHER: 'OTHER',
}

/** Selectable document types, in display order. */
export const KYC_DOCUMENT_TYPES: readonly KycDocumentType[] = [
  KYC_DOCUMENT_TYPE.ID_CARD,
  KYC_DOCUMENT_TYPE.PASSPORT,
  KYC_DOCUMENT_TYPE.RESIDENCE_PERMIT,
  KYC_DOCUMENT_TYPE.OTHER,
]

/**
 * Document types that need a back side as well (unless a PDF copy is handed
 * in). Mirrors `TWO_SIDED_DOCUMENT_TYPES` of the API — a passport has all data
 * on one page.
 */
export const KYC_TWO_SIDED_DOCUMENT_TYPES: readonly KycDocumentType[] = [
  KYC_DOCUMENT_TYPE.ID_CARD,
  KYC_DOCUMENT_TYPE.RESIDENCE_PERMIT,
]

/** `accept` of the upload field — photos plus a PDF copy/scan. */
export const KYC_UPLOAD_ACCEPT =
  'image/jpeg,image/png,image/heic,image/heif,application/pdf,.heic,.heif'

/** MIME types the API accepts (it checks the magic bytes, not this list). */
export const KYC_UPLOAD_MIME_TYPES: readonly string[] = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heic',
  'image/heif',
  'application/pdf',
]

/** Extensions for files the browser reports without a MIME type (iPhone HEIC). */
export const KYC_UPLOAD_EXTENSIONS: readonly string[] = [
  '.jpg',
  '.jpeg',
  '.png',
  '.heic',
  '.heif',
  '.pdf',
]

/** Upload limit of the API (`KYC_UPLOAD_MAX_BYTES`) — 15 MB. */
export const KYC_UPLOAD_MAX_BYTES = 15 * 1024 * 1024

/** JPEG quality of a camera capture — readable document, moderate size. */
export const KYC_CAPTURE_JPEG_QUALITY = 0.92

/** ID-1 card format (ISO/IEC 7810): 85.6 × 54 mm. */
export const KYC_ID1_ASPECT_RATIO = 85.6 / 54 // ≈ 1.586

/** Share of the visible area the document frame may take (width or height). */
export const KYC_DOCUMENT_FRAME_FILL = 0.94

/** Extra margin around the frame that is kept in the crop, per side (share of frame size). */
export const KYC_DOCUMENT_CROP_MARGIN = 0.04

/**
 * Minimal long edge of a stored capture in pixels — below that a hint is shown.
 *
 * An ID-1 card is 85.6 mm long, so 300 dpi ≈ 1011 px: enough for the reviewer
 * to read it and for the extraction. A stricter value only produced a warning
 * on every single capture (Issue #1116).
 */
export const KYC_CAPTURE_MIN_LONG_EDGE_PX = 1000

/**
 * Resolution we ask the camera for — `ideal`, never `min`: a weaker camera must
 * still deliver what it has instead of failing outright (Issue #1116).
 */
export const KYC_CAPTURE_IDEAL_WIDTH_PX = 3840
export const KYC_CAPTURE_IDEAL_HEIGHT_PX = 2160

/**
 * How long `ImageCapture.takePhoto()` may take before the video frame is used
 * instead — some devices never resolve it.
 */
export const KYC_CAPTURE_TAKE_PHOTO_TIMEOUT_MS = 3000

/** Long edge a capture is scaled down to if the JPEG exceeds the upload limit. */
export const KYC_CAPTURE_FALLBACK_LONG_EDGE_PX = 3000

/** Random tasks for the live selfie; the server picks one. */
export const KYC_LIVENESS_CHALLENGE_KEYS: readonly KycLivenessChallenge[] = [
  'TURN_HEAD_LEFT',
  'TURN_HEAD_RIGHT',
  'LOOK_UP',
  'SHOW_TWO_FINGERS',
  'SHOW_FINGERS',
]

/** Range the server picks the finger number from (`SHOW_FINGERS`, Issue #1120). */
export const KYC_LIVENESS_FINGER_MIN = 1
export const KYC_LIVENESS_FINGER_MAX = 5

/**
 * Seconds between «Gesicht ruhig im Oval» and the release, whenever a finger
 * task is set (Issue #1144).
 *
 * The face detection only knows whether somebody sits in the oval — it says
 * nothing about the fingers. Releasing right away produced the selfie before
 * the hand was even up, which makes the liveness proof worthless. These seconds
 * are the customer's time to actually show the fingers.
 */
export const KYC_SELFIE_COUNTDOWN_S = 3

/** One step of that countdown — 3 → 2 → 1, a second each. */
export const KYC_SELFIE_COUNTDOWN_STEP_MS = 1000

// ---------------------------------------------------------------------------
// Automatic reading of the document pictures (Issue #1165)
// ---------------------------------------------------------------------------

export const KYC_ANALYSIS_STATUS: {
  readonly [K in Uppercase<KycAnalysisStatusValue>]: Lowercase<K>
} = {
  NONE: 'none',
  PENDING: 'pending',
  DONE: 'done',
  FAILED: 'failed',
  DISABLED: 'disabled',
}

export const KYC_IMAGE_QUALITY: {
  readonly [K in Uppercase<KycImageQualityValue>]: Lowercase<K>
} = {
  OK: 'ok',
  GLARE: 'glare',
  BLURRY: 'blurry',
  CUT_OFF: 'cut_off',
  NOT_DOCUMENT: 'not_document',
}

/** Poll interval of the status while the analysis is running. */
export const KYC_ANALYSIS_POLL_INTERVAL_MS = 3000

/**
 * How long the waiting circle stands alone before the way out appears.
 *
 * Owner decision 06.10.2026: «das dauert halt eine Weile, kein Thema» — but
 * nobody is held hostage by our AI, so after half a minute the customer may
 * fill the three fields in by hand.
 */
export const KYC_ANALYSIS_SKIP_AFTER_MS = 30_000

/** Hard ceiling of the wait — after this the form opens by itself. */
export const KYC_ANALYSIS_TIMEOUT_MS = 180_000

// ---------------------------------------------------------------------------
// Phone handoff: QR code on the desktop, capture on the phone (Issue #1071)
// ---------------------------------------------------------------------------

/** Header carrying the handoff session on the public phone endpoints. */
export const KYC_SESSION_HEADER = 'X-KYC-Session'

/** Poll interval of the desktop dialog while the QR code is on screen. */
export const KYC_HANDOFF_POLL_INTERVAL_MS = 3000

/** How long a handoff code is valid — the API decides, this is for the text. */
export const KYC_HANDOFF_VALIDITY_MINUTES = 15

/**
 * Whether this device is most likely a phone or tablet.
 *
 * A coarse pointer means finger instead of mouse, and a camera in the same
 * hand: showing a QR code to scan with the very device it is displayed on
 * would be absurd, so the assistant starts right away there.
 */
export function isLikelyMobileDevice(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(pointer: coarse)').matches
}

/** Date the revised Swiss anti-money-laundering duties take effect. */
export const GWG_EFFECTIVE_DATE = '2026-10-01'

/**
 * Tense of the legal hint: «Ab dem 1. Oktober 2026» before that date,
 * «Seit dem 1. Oktober 2026» from that date on.
 *
 * The dialog texts were written in the past tense while the law was still
 * ahead of us — this picks the right variant instead.
 */
export function gwgEffectiveWording(now: Date = new Date()): 'before' | 'after' {
  return now < new Date(`${GWG_EFFECTIVE_DATE}T00:00:00`) ? 'before' : 'after'
}
